import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Grid,
  Box,
  IconButton,
  Checkbox,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Notification from "../../../components/Notification";
import ConfirmDialog from "../../../components/ConfirmDialog";
import apiUtils from "../../../utils/apiUtils";
import ViewBankAccountDialog from "../../../components/ViewBankAccountDialog";
import AddBankAccount from "../../../components/AddBankAccount";
import DataGridExport from "../../../components/DataGridExport";
import { usePermissions } from "../../../hooks/usePermissions";
import { CAPABILITIES } from "../../../auth/permissions";

const CustomerBankAccountsGrid = ({ customerId, onBankAccountsChange, gridHeight = 307 }) => {
  const { hasPermission } = usePermissions();
  const canWriteBankAccounts = hasPermission(CAPABILITIES.bankAccountWrite, "W");
  const countryData = apiUtils.useApiData("/api/v1/countries");

  const [tableBankAccounts, setTableBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [isUpdatingDefault, setIsUpdatingDefault] = useState(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [bankAccountDefaultDialog, setBankAccountDefaultDialog] = useState({
    isOpen: false,
    title: "Change Default Bank Account",
    subTitle: "Are you sure you want to change the default bank account?",
    bankAccountId: null,
    onConfirm: () => handleDefaultChangeConfirmed(),
  });
  const [viewBankAccountDialog, setViewBankAccountDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    test: [],
  });

  const fetchBankAccounts = useCallback(async () => {
    if (!customerId) {
      setTableBankAccounts([]);
      return;
    }

    try {
      setLoadingBankAccounts(true);
      const data = await apiUtils.fetchData(`/api/v1/bankaccountsByEntityId/${customerId}`);
      setTableBankAccounts(data);
      onBankAccountsChange?.(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingBankAccounts(false);
    }
  }, [customerId, onBankAccountsChange]);

  useEffect(() => {
    fetchBankAccounts();
  }, [fetchBankAccounts]);

  const handleDefaultChange = (bankAccountId) => {
    setBankAccountDefaultDialog({
      ...bankAccountDefaultDialog,
      isOpen: true,
      bankAccountId,
      onConfirm: () => handleDefaultChangeConfirmed(bankAccountId),
    });
  };

  const handleDefaultChangeConfirmed = async (bankAccountId) => {
    try {
      setIsUpdatingDefault(true);

      const updatedAccounts = tableBankAccounts.map((account) => ({
        ...account,
        isDefault: account.bankAccountId === bankAccountId,
      }));
      setTableBankAccounts(updatedAccounts);

      const response = await axios.patch(`/api/v1/BankAccounts/${bankAccountId}/default`, { isDefault: true });

      if (response.status === 204) {
        setNotify({
          isOpen: true,
          message: "Default bank account has been updated successfully",
          type: "success",
        });
        onBankAccountsChange?.(updatedAccounts);
      }
    } catch (error) {
      console.error("Error updating bank account:", error);
      await fetchBankAccounts();
      setNotify({
        isOpen: true,
        message: "Failed to update default bank account",
        type: "error",
      });
    } finally {
      setIsUpdatingDefault(false);
      setBankAccountDefaultDialog({ ...bankAccountDefaultDialog, isOpen: false });
    }
  };

  const onDeleteBankAccountConfirmed = async (e, params) => {
    try {
      await apiUtils.deleteData(`/api/v1/bankaccounts/${params.bankAccountId}`);
      setNotify({
        isOpen: true,
        message: "Deleted Successfully",
        type: "success",
      });
      fetchBankAccounts();
    } catch (error) {
      console.error(error);
    }

    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const onEditConfirmed = () => {
    setViewBankAccountDialog({ ...viewBankAccountDialog, isOpen: false });
  };

  const columnsBankAccounts = [
    {
      field: "bankAccountId",
      headerName: "BankAccountId",
      width: 50,
      hide: true,
    },
    { field: "iban", headerName: "IBAN", flex: 1 },
    {
      field: "countryCode",
      headerName: "Country",
      flex: 0.3,
      valueGetter: (params) => countryData.find((country) => country.countryId === params.row.countryCode)?.countryCode,
    },
    {
      field: "isDefault",
      headerName: "Active Bank Account",
      flex: 0.3,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.isDefault}
          onChange={() => handleDefaultChange(params.row.bankAccountId)}
          disabled={isUpdatingDefault || !canWriteBankAccounts}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.3,
      renderCell: (params) => (
        <Box>
          <IconButton
            aria-label="Delete bank account"
            disabled={!canWriteBankAccounts}
            onClick={() => {
              setConfirmDialog({
                isOpen: true,
                title: "Delete Bank Account",
                subTitle: `Are you sure you want to delete ${params.row.iban}?`,
                onConfirm: () => onDeleteBankAccountConfirmed(null, params.row),
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              setViewBankAccountDialog({
                isOpen: true,
                title: "Edit Bank Account",
                params: params.row,
                customerId,
                test: [],
                subTitle: `Edit ${params.row.iban}?`,
                onConfirm: () => onEditConfirmed(),
              });
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ width: "100%", position: "relative" }}>
        <Notification notify={notify} setNotify={setNotify} />
      </Box>
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
      <ConfirmDialog confirmDialog={bankAccountDefaultDialog} setConfirmDialog={setBankAccountDefaultDialog} />
      <ViewBankAccountDialog
        viewBankAccountDialog={viewBankAccountDialog}
        setViewBankAccountDialog={setViewBankAccountDialog}
        onBankAccountUpdate={fetchBankAccounts}
      />

      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item xs={6} sx={{ margin: 0, padding: 0 }}>
          {canWriteBankAccounts && (
            <AddBankAccount onDataRefresh={fetchBankAccounts} customerId={customerId} iconOnly />
          )}
        </Grid>
        <Grid item xs={6} sx={{ margin: 0, padding: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <DataGridExport tableData={tableBankAccounts} columns={columnsBankAccounts} />
          </Box>
        </Grid>
      </Grid>

      <Box position="relative" sx={{ mt: 1 }}>
        <DataGrid
          getRowId={(row) => row.bankAccountId}
          disableColumnFilter
          disableSelectionOnClick
          disableColumnSelector
          loading={loadingBankAccounts}
          components={{ Toolbar: GridToolbar }}
          density="compact"
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: false },
              printOptions: { disableToolbarButton: false },
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 250 },
            },
          }}
          rows={tableBankAccounts}
          columns={columnsBankAccounts}
          pageSize={10}
          sx={{
            height: gridHeight,
            width: "100%",
            borderRadius: 1,
            backgroundColor: "background.paper",
            boxShadow: 2,
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
          }}
        />
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.1)",
          }}
          open={isUpdatingDefault}
        >
          <CircularProgress color="primary" />
        </Backdrop>
      </Box>
    </>
  );
};

export default CustomerBankAccountsGrid;
